"use client"

import styled from "@emotion/styled"
import { extractProperty } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { whyUsIconClicked } from "@eurodrive/utils/gtm"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import Container from "./Container"
import { sharedColors } from "./theme/colors"
import { TransitionUp } from "./transitions/TransitionUp"
import { useInViewConfig } from "./transitions/utils"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 64px 0;
  background-color: #f3f3f3;
  ${({ theme }) => theme.breakpoints.up("md")} {
    text-align: center;
  }
`

const InnerLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 16px;
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const StyledItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
  padding: 32px 16px;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid ${sharedColors.lightGrey};
  background: white;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    align-items: center;
    justify-content: flex-start;
    min-height: 255px;
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    text-align: center;
  }
`

export default function ReasonsWhy({ data }: { data: any }) {
  const title = extractProperty(data, "title") as string
  const itemsList = extractProperty(data, "itemsList")

  const items = itemsList?.map((item: any) => ({
    iconUrl: extractProperty(item, "icon"),
    title: extractProperty(item, "title"),
    text: extractProperty(item, "text"),
  }))
  const { ref: inViewRef, inView } = useInView(useInViewConfig)

  return (
    <StyledLayout ref={inViewRef}>
      <Container paddingMobile>
        <InnerLayout>
          <TransitionUp $inView={inView}>
            <Typography variant="h2">{title}</Typography>
          </TransitionUp>
          <StyledGrid>
            {items.map((el: any, index: number) => (
              <TransitionUp $inView={inView} $delay={index * 0.2} key={index}>
                <StyledItem onClick={() => whyUsIconClicked(el.title)}>
                  <Image
                    src={getUmbracoMediaUrl(el.iconUrl)}
                    alt=""
                    width={40}
                    height={40}
                  />

                  <TextBox>
                    <Typography variant="h3">{el.title}</Typography>
                    <Typography variant="body2">{el.text}</Typography>
                  </TextBox>
                </StyledItem>
              </TransitionUp>
            ))}
          </StyledGrid>
        </InnerLayout>
      </Container>
    </StyledLayout>
  )
}
